import { Loader2, PencilIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useCreateNoteMutation } from '~entities/notes/api/create-note';
import { useUpdateNote } from '~entities/notes/api/update-note';
import { NoteEntityType } from '~entities/notes/model/note';
import { useStudioStore } from '~entities/studios/model/studio-store';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '~shared/shadcn/ui/form';
import { Textarea } from '~shared/shadcn/ui/textarea';

type AddEditNoteProps =
  | { entityId: string; noteId?: undefined; note?: undefined }
  | { entityId?: undefined; noteId: string; note: string };

export const AddEditNote = (props: AddEditNoteProps) => {
  const entityId = props.entityId;
  const noteId = props.noteId;
  // note is only present in edit mode, always defined there
  const note = 'note' in props ? props.note : '';
  const [open, setOpen] = useState(false);
  const studio = useStudioStore();
  const createNoteMutation = useCreateNoteMutation();
  const updateNoteMutation = useUpdateNote();
  const form = useForm<{ note: string }>({
    defaultValues: { note },
  });

  const setDialogOpen = (v: boolean) => {
    setOpen(v);
    if (!v) form.reset({ note: noteId ? note : '' });
  };

  const onSubmit = async (data: { note: string }) => {
    try {
      if (noteId) {
        await updateNoteMutation.mutateAsync({ noteId, note: data.note });
        toast.success('Note updated successfully');
      } else if (entityId) {
        await createNoteMutation.mutateAsync({
          note: data.note,
          entityId,
          entityType: NoteEntityType.Student,
          studioId: studio.currentStudioId ?? '',
        });
        toast.success('Note created successfully');
      }
      form.reset();
      setDialogOpen(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      form.reset();
      setDialogOpen(false);
      console.error(error);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (error?.response?.data?.message) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        toast.error(error.response.data.message);
      } else {
        toast.error(noteId ? 'Failed to update Note' : 'Failed to create Note');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button
          variant={noteId ? 'default' : 'secondary'}
          className={cn(noteId && 'size-6 p-0 text-secondary [&_svg]:size-3')}
        >
          {noteId ? <PencilIcon /> : 'Add Note'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{noteId ? 'Edit Note' : 'Create Note'}</DialogTitle>
        </DialogHeader>
        <div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name='note'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Note</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder='Enter your note...'
                        {...field}
                        rows={20}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Close</Button>
          </DialogClose>
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={
              !form.watch('note') ||
              createNoteMutation.isPending ||
              updateNoteMutation.isPending
            }
          >
            {createNoteMutation.isPending || updateNoteMutation.isPending ? (
              <div className='flex gap-2'>
                <Loader2 className='animate-spin' /> Saving
              </div>
            ) : (
              <div>Save</div>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
